import React, { useState, useEffect } from "react";
import {
  Modal,
  Button,
  Container,
  Row,
  Col,
  Form,
  Badge,
} from "react-bootstrap";
import { formatCurrency } from "../utils/formatCurrency";
import { formatDateInTimezone } from "../utils/formatDateInTimezone";
import config from "../config";
import { generatePaymentToken } from "../services/paymentServices";
import { companyID } from "../auth"; 
import api from "../api/axios";
import { RxCross2 } from "react-icons/rx";

const PlanConfigurationModal = ({
  show,
  onHide,
  plans,
  selectedPlanId,
  onPaymentSuccess,
  modalType = "subscription", // "subscription" or "renewal"
  currentPlan = null,
}) => {
  console.log("plans", plans);
  console.log("currentPlan" , currentPlan);
   console.log("selectedPlanId" , selectedPlanId);

  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedDuration, setSelectedDuration] = useState(null);
  
  const [modalSelections, setModalSelections] = useState({
    duration_id: null,
    option_id: null,
    user_count: 1,
    total_cost: 0,
  });
  const [error, setError] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [subscriptionLimit, setSubscriptionLimit] = useState(null);

  useEffect(() => {
    const fetchSubscriptionLimit = async () => {
      try {
        const company_id = companyID();
        const response = await api.get(
          `subscriptions/subscription-limit?company_id=${company_id}`
        );
        if (response.data.success) {
          setSubscriptionLimit(response.data.data);
        }
      } catch (err) {
        console.error("Failed to fetch subscription limit:", err);
      }
    };

    if (show) {
      fetchSubscriptionLimit();
    }
  }, [show]);
  const isSubscriptionExpired = JSON.parse(
    localStorage.getItem("isSubscriptionExpired")
  );

  useEffect(() => {
    if (selectedPlanId && plans.length > 0) {
      const plan = plans.find((p) => p.plan_id === selectedPlanId);
      setSelectedPlan(plan);

      if (plan) {
        // Initialize modal with first duration and option
        const firstDuration = plan.plan_duration?.[0];

        // Filter options based on current employee count
        const validOptions =
          plan.options?.filter(
            (option) =>
              !subscriptionLimit ||
              option.max_persons >= subscriptionLimit.currentEmployeeCount
          ) || [];
        const firstOption = validOptions[0] || plan.options?.[0];
        const initialUserCount = firstOption?.max_persons || 1;

        setSelectedDuration(firstDuration);
        setModalSelections({
          duration_id: firstDuration?.period_id || null,
          option_id: firstOption?.option_id || null,
          user_count: initialUserCount,
          total_cost: calculateModalCost(firstDuration, initialUserCount),
        });
      }
    }
  }, [selectedPlanId, plans, currentPlan, subscriptionLimit]);

  const isSpecialPriceValid = (
    specialPriceStartDate,
    specialPriceEndDate,
    checkDate = new Date()
  ) => {
    const pad = (n) => String(n).padStart(2, "0");
    const formattedCheckDate = [
      checkDate.getFullYear(),
      pad(checkDate.getMonth() + 1),
      pad(checkDate.getDate()),
    ].join("-");

    return (
      formattedCheckDate >= specialPriceStartDate &&
      formattedCheckDate <= specialPriceEndDate
    );
  };

  const calculateModalCost = (duration, userCount) => {
    if (!duration || !userCount) return 0;

    const {
      special_price,
      renewal_price,
      base_price_per_person,
      special_price_start_date,
      special_price_end_date,
    } = duration;

    const isSpecialValid =
      special_price &&
      special_price_start_date &&
      special_price_end_date &&
      isSpecialPriceValid(special_price_start_date, special_price_end_date);

    // Use different base price based on modal type
    const basePrice = modalType === "due" ? base_price_per_person : renewal_price;

    const pricePerUser =
      isSpecialValid && basePrice > special_price
        ? special_price
        : basePrice;

    return (pricePerUser * userCount * duration.duration).toFixed(2);
  };

  const handleModalDurationChange = (durationId) => {
    const duration = selectedPlan.plan_duration.find(
      (d) => d.period_id === parseInt(durationId)
    );
    setSelectedDuration(duration);
    const newCost = calculateModalCost(duration, modalSelections.user_count);

    setModalSelections((prev) => ({
      ...prev,
      duration_id: parseInt(durationId),
      total_cost: newCost,
    }));
  };

  const handleModalOptionChange = (optionId) => {
    const option = selectedPlan.options.find(
      (o) => o.option_id === parseInt(optionId)
    );
    const userCount = option?.max_persons || 1;
    const newCost = calculateModalCost(selectedDuration, userCount);

    setModalSelections((prev) => ({
      ...prev,
      option_id: parseInt(optionId),
      user_count: userCount,
      total_cost: newCost,
    }));
  };

  const handleProcessPayment = async () => {
    if (
      !selectedPlan ||
      !modalSelections.duration_id ||
      !modalSelections.user_count
    ) {
      setError("Please select all required options.");
      return;
    }

    try {
      setError("");
      setIsProcessing(true);

      const company_id = companyID();
      const res = await generatePaymentToken({
        paymentDetails: {
          plan_id: selectedPlan?.plan_id,
          option_id: modalSelections?.option_id,
          plan_period: modalSelections?.duration_id,
          user_count: modalSelections?.user_count,
          total_cost: modalSelections?.total_cost,
          modal_type: modalType,
          current_subscription_id: currentPlan?.subscription_id || null,
        },
        redirectUrl: config.APP_URL + "payment-confirmation",
        company_id: company_id,
      });

      if (res && res.data.redirectUrl) {
        window.location.href = res.data.redirectUrl;
      } else {
        setError(`Failed to initiate ${modalType === "due" ? "payment" : modalType}. Please try again.`);
      }
    } catch (err) {
      console.log(err.message);
      setError(`Failed to initiate ${modalType === "due" ? "payment" : modalType}. Please try again.`);
    } finally {
      setIsProcessing(false);
    }
  };

  const getModalTitle = () => {
    if (modalType === "renewal") {
      return `Renew Your ${selectedPlan?.plan_name} Plan`;
    }
    if (modalType === "due") {
      return `Pay Outstanding Amount - ${selectedPlan?.plan_name} Plan`;
    }
    return `Configure Your ${selectedPlan?.plan_name} Plan`;
  };

  const getButtonText = () => {
    if (modalType === "renewal") {
      return isProcessing ? "Processing Renewal..." : "Proceed to Renewal";
    }
    if (modalType === "due") {
      return isProcessing ? "Processing Payment..." : "Pay Outstanding Amount";
    }
    return isProcessing ? "Processing Payment..." : "Proceed to Payment";
  };
 
  return (
    <Modal show={show} size="lg" centered backdrop="static">
      <Modal.Header >
        <Modal.Title>{getModalTitle()}</Modal.Title>
       { !isSubscriptionExpired && <button
                type="button"
                className="btn-close"
                onClick={onHide}
              >
                <span  ><RxCross2  className="mb-2 "/></span>
              </button>}
      </Modal.Header>
      <Modal.Body>
        {selectedPlan && selectedDuration && (
          <Container>
            {currentPlan && modalType === "renewal" && (
              <Row className="mb-4">
                <Col>
                  <div className="alert alert-light border">
                    <h6 className="mb-3">Current Subscription Details</h6>
                    <Row>
                      <Col md={6}>
                        <div className="mb-2">
                          <strong>Plan:</strong> {currentPlan.plan_name}
                        </div>
                        <div className="mb-2">
                          <strong>Users:</strong> {currentPlan.total_persons}
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="mb-2">
                          <strong>Start Date:</strong>{" "}
                          {currentPlan.start_date
                            ? formatDateInTimezone(currentPlan.start_date)
                            : "-"}
                        </div>
                        <div className="mb-2">
                          <strong>End Date:</strong>{" "}
                          {currentPlan.end_date
                            ? formatDateInTimezone(currentPlan.end_date)
                            : "-"}
                        </div>
                      </Col>
                    </Row>
                  </div>
                </Col>
              </Row>
            )}

            <Row>
              <Col md={6}>
                <h5 className="mb-3">
                  {modalType === "renewal"
                    ? "New Plan Configuration"
                    : modalType === "due"
                    ? "Outstanding Payment Details"
                    : "Plan Details"}
                </h5>
                <div className="mb-3">
                  <strong>Plan:</strong> {selectedPlan.plan_name}
                  {selectedPlan.most_popular && (
                    <Badge bg="warning" className="ms-2">
                      Most Popular
                    </Badge>
                  )}
                </div>
                <div className="mb-3">
                  <strong>Description:</strong>{" "}
                  {selectedPlan.plan_description || "Premium HR solution"}
                </div>

                <Form.Group className="mb-3">
                  <Form.Label>Select Duration</Form.Label>
                  <Form.Select
                    value={modalSelections.duration_id || ""}
                    onChange={(e) => handleModalDurationChange(e.target.value)}
                  >
                    {selectedPlan.plan_duration?.map((duration) => {
                      const basePrice = modalType === "due" ? duration.base_price_per_person : duration.renewal_price;
                      const hasSpecialPrice =
                        duration.special_price &&
                        isSpecialPriceValid(
                          duration.special_price_start_date,
                          duration.special_price_end_date
                        ) &&
                        basePrice > duration.special_price;

                      return (
                        <option
                          key={duration.period_id}
                          value={duration.period_id}
                        >
                          {duration.period_title} ({duration.duration} months) -{" "}
                          {hasSpecialPrice
                            ? `${formatCurrency(
                                duration.special_price
                              )}/user/month (Save ${Math.round(
                                ((basePrice -
                                  duration.special_price) /
                                  basePrice) *
                                  100
                              )}%)`
                            : `${formatCurrency(
                                basePrice
                              )}/user/month`}
                        </option>
                      );
                    })}
                  </Form.Select>
                </Form.Group>

                {selectedPlan.options && selectedPlan.options.length > 0 && (
                  <Form.Group className="mb-3">
                    <Form.Label>Select User Range</Form.Label>
                    <Form.Select
                      value={modalSelections.option_id || ""}
                      onChange={(e) => handleModalOptionChange(e.target.value)}
                    >
                      {selectedPlan.options
                        .filter(
                          (option) =>
                            !subscriptionLimit ||
                            option.max_persons >= subscriptionLimit.currentEmployeeCount
                        )
                        .map((option) => {
                          const isCurrentEmployeeCount =
                            subscriptionLimit &&
                            option.max_persons < subscriptionLimit.currentEmployeeCount;

                          return (
                            <option
                              key={option.option_id}
                              value={option.option_id}
                              disabled={isCurrentEmployeeCount}
                            >
                              {option.min_persons === 0
                                ? "Up to"
                                : `${option.min_persons} to`}{" "}
                              {option.max_persons} users
                              {isCurrentEmployeeCount &&
                                " (Below current employee count)"}
                            </option>
                          );
                        })}
                    </Form.Select>
                    {subscriptionLimit && (
                      <Form.Text className="text-muted">
                        Current employees: {subscriptionLimit.currentEmployeeCount}
                      </Form.Text>
                    )}
                  </Form.Group>
                )}
              </Col>

              <Col md={6}>
                <h5 className="mb-1">Cost Breakdown</h5>
                {(() => {
                  const basePrice = modalType === "due" ? selectedDuration.base_price_per_person : selectedDuration.renewal_price;
                  const isSpecialValid =
                    selectedDuration.special_price &&
                    isSpecialPriceValid(
                      selectedDuration.special_price_start_date,
                      selectedDuration.special_price_end_date
                    );
                  const hasDiscount =
                    isSpecialValid &&
                    basePrice >
                      selectedDuration.special_price;
                  const pricePerUser = hasDiscount
                    ? selectedDuration.special_price
                    : basePrice;

                  return (
                    <>
                      <div className="mb-2">
                        <strong>Duration:</strong>{" "}
                        {selectedDuration.period_title} (
                        {selectedDuration.duration} months)
                      </div>
                      <div className="mb-2">
                        <strong>Users:</strong> {modalSelections.user_count}
                      </div>
                      <div className="mb-2">
                        <strong>Price per user per month:</strong>{" "}
                        {hasDiscount ? (
                          <>
                            <span className="text-decoration-line-through text-muted">
                              {formatCurrency(
                                basePrice
                              )}
                            </span>{" "}
                            <span className="text-success">
                              {formatCurrency(selectedDuration.special_price)}
                            </span>
                            <Badge bg="success" className="ms-2">
                              Save{" "}
                              {Math.round(
                                ((basePrice -
                                  selectedDuration.special_price) /
                                  basePrice) *
                                  100
                              )}
                              %
                            </Badge>
                          </>
                        ) : (
                          formatCurrency(pricePerUser)
                        )}
                      </div>
                      <hr />
                      <div className="mb-2">
                        <strong>Calculation:</strong>
                        <br />
                        {formatCurrency(pricePerUser)} ×{" "}
                        {modalSelections.user_count} users ×{" "}
                        {selectedDuration.duration} months
                      </div>
                    </>
                  );
                })()}
                <div className="alert alert-info">
                  <h4 className="mb-0">
                    Total: {formatCurrency(modalSelections.total_cost)}
                  </h4>
                </div>
              </Col>
            </Row>
            {error && (
              <Row>
                <Col>
                  <div className="alert alert-danger mt-3">{error}</div>
                </Col>
              </Row>
            )}
          </Container>
        )}
      </Modal.Body>
      <Modal.Footer>
       { !isSubscriptionExpired   &&<Button variant="secondary" onClick={onHide} disabled={isProcessing}>
          Cancel
        </Button>}
        <Button
          variant="primary"
          onClick={handleProcessPayment}
          disabled={
            !modalSelections.user_count ||
            modalSelections.user_count === 0 ||
            isProcessing
          }
        >
          {getButtonText()}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default PlanConfigurationModal;
